import { AuthProviders, ProviderPriority, SocialProviders } from '../constants/authProviders'
import { PasswordAuthProvider } from './PasswordAuthProvider'
import { EmailTokenAuthProvider } from './EmailTokenAuthProvider'
import { MagicLinkAuthProvider } from './MagicLinkAuthProvider'
import { GoogleAuthProvider } from './GoogleAuthProvider'
import { FacebookAuthProvider } from './FacebookAuthProvider'
import { AppleAuthProvider } from './AppleAuthProvider'
import { JwtAuthProvider } from './JwtAuthProvider'
import { SsoAuthProvider } from './SsoAuthProvider'
import { IbiAuthProvider } from './IbiAuthProvider'

/**
 * Authentication Provider Factory
 * Manages provider instances and determines which are enabled
 */
export class AuthProviderFactory {
    constructor() {
        // Provider instances will be created lazily
        this._providers = {}

        // Provider class registry (lazy loaded)
        this._providerClasses = {
            [AuthProviders.PASSWORD]: null,
            [AuthProviders.EMAIL_TOKEN]: null,
            [AuthProviders.MAGIC_LINK]: null,
            [AuthProviders.GOOGLE]: null,
            [AuthProviders.FACEBOOK]: null,
            [AuthProviders.APPLE]: null,
            [AuthProviders.JWT]: null,
            [AuthProviders.SSO]: null,
            [AuthProviders.IBI]: null
        }
    }

    /**
     * Create a factory instance with all providers registered
     * This should be called by the store to get a fully configured factory
     * @returns {AuthProviderFactory}
     */
    static createWithProviders() {
        const factory = new AuthProviderFactory()

        // Register all providers synchronously
        factory.registerProvider(AuthProviders.JWT, JwtAuthProvider)
        factory.registerProvider(AuthProviders.SSO, SsoAuthProvider)
        factory.registerProvider(AuthProviders.IBI, IbiAuthProvider)
        factory.registerProvider(AuthProviders.MAGIC_LINK, MagicLinkAuthProvider)
        factory.registerProvider(AuthProviders.EMAIL_TOKEN, EmailTokenAuthProvider)
        factory.registerProvider(AuthProviders.PASSWORD, PasswordAuthProvider)
        factory.registerProvider(AuthProviders.GOOGLE, GoogleAuthProvider)
        factory.registerProvider(AuthProviders.FACEBOOK, FacebookAuthProvider)
        factory.registerProvider(AuthProviders.APPLE, AppleAuthProvider)


        return factory
    }

    /**
     * Register a provider class (for lazy loading)
     * @param {string} method - Provider method identifier
     * @param {Function} ProviderClass - Provider class constructor
     */
    registerProvider(method, ProviderClass) {
        this._providerClasses[method] = ProviderClass
    }

    /**
     * Get provider instance (lazy instantiation)
     * @param {string} method - Provider method identifier
     * @returns {BaseAuthProvider|null} Provider instance or null
     */
    getProvider(method) {
        // Return cached instance if exists
        if (this._providers[method]) {
            return this._providers[method]
        }

        // Instantiate if class registered
        const ProviderClass = this._providerClasses[method]
        if (ProviderClass) {
            this._providers[method] = new ProviderClass()
            return this._providers[method]
        }

        console.warn(`Provider "${method}" not registered`)
        return null
    }

    /**
     * Get all registered provider instances
     * @returns {Array<BaseAuthProvider>}
     */
    getAllProviders() {
        return Object.keys(this._providerClasses)
            .map(method => this.getProvider(method))
            .filter(Boolean)
    }

    /**
     * Read enabled providers from .env
     * This defines the "maximum possible" set of providers
     * @returns {Array<string>} List of provider method identifiers
     */
    getEnvProviders() {
        const envVar = process.env.VUE_APP_AUTH_PROVIDERS

        if (envVar) {
            const providers = envVar
                .split(',')
                .map(p => p.trim())
                .filter(Boolean)
            return providers
        }

        // Default providers if not specified in .env
        return [
            AuthProviders.PASSWORD,
            AuthProviders.GOOGLE,
            AuthProviders.FACEBOOK,
            AuthProviders.APPLE
        ]
    }

    /**
     * Build context object for provider isEnabled() decisions
     * Called by Vuex store with rootState and rootGetters
     *
     * @param {Object} rootState - Vuex root state
     * @param {Object} rootGetters - Vuex root getters
     * @param {Object} queryParams - Optional URL query params (from $route.query)
     * @returns {Object} Context for provider decisions
     */
    buildContext(rootState, rootGetters, queryParams = {}) {
        return {
            // Providers from .env (maximum possible set)
            envProviders: this.getEnvProviders(),

            // Current subdomain
            subdomain: rootGetters['thirdLevelDomain'],

            // URL query parameters (for JWT/SSO detection)
            queryParams: queryParams || {},

            // Context parameters from external sources (JWT, preview, etc.)
            contextParams: rootState.auth.contextParams || {},

            // Backend configuration
            // Only properties used by providers should be included here
            appConfig: {
                disable_social_login: rootGetters['appConfig/disableSocialLogin'] || false
            }
        }
    }

    /**
     * Get list of enabled provider methods based on context
     * Each provider decides if it's enabled via isEnabled(context)
     *
     * @param {Object} context - Context from buildContext()
     * @returns {Array<string>} List of enabled provider method identifiers
     */
    getEnabled(context) {
        return this.getAllProviders()
            .filter(provider => provider.isEnabled(context))
            .map(provider => provider.getMethod())
    }

    /**
     * Check if a specific provider is enabled
     * @param {string} method - Provider method identifier
     * @param {Object} context - Context from buildContext()
     * @returns {boolean}
     */
    isEnabled(method, context) {
        const provider = this.getProvider(method)
        return provider ? provider.isEnabled(context) : false
    }

    /**
     * Check if any social provider is enabled
     * @param {Object} context - Context from buildContext()
     * @returns {boolean}
     */
    hasSocial(context) {
        const enabled = this.getEnabled(context)
        return enabled.some(method => SocialProviders.includes(method))
    }

    /**
     * Get the primary (highest priority) non-social provider
     * Based on ProviderPriority order
     * Social providers are excluded as they're shown separately
     *
     * @param {Object} context - Context from buildContext()
     * @returns {string|null} Primary provider method or null
     */
    getPrimary(context) {
        const enabled = this.getEnabled(context)

        // Find first enabled provider from priority list (excluding social)
        for (const provider of ProviderPriority) {
            if (!SocialProviders.includes(provider) && enabled.includes(provider)) {
                return provider
            }
        }

        return null
    }
}

export default AuthProviderFactory
